// Project visibility: private -> team (RFC-1371 D3).
//
// A pill in the workspace header showing the current state, and — when the
// server says this user may publish — a confirmation dialog to do it.
//
// ── Where the state comes from ─────────────────────────────────────────────
//
// `visibility`, `owner_team_id` and `can_publish` ride on the existing
// `GET /projects/{id}` payload, so this costs no extra fetch. `can_publish` is
// computed server-side from the same predicate the write enforces, and is
// taken at face value. Re-deriving §6 here would duplicate a permission rule
// in a second place and guarantee eventual drift; if the server says false,
// the write will refuse regardless of what this component concluded.
//
// The owning team's NAME is resolved from the user's own team list rather than
// requested, which is sound precisely because of §6: you can only see a
// project through membership of its owning team, so that team is always in
// your list.
//
// ── Why there is no way back ───────────────────────────────────────────────
//
// D3 makes the transition ONE-WAY. There is no "make private" control, no
// disabled one, and no toggle: a switch that only moves in one direction reads
// as a bug, and a user who flips it expecting to flip it back has been misled
// by the affordance rather than by the copy. Once published the pill is inert
// text.
//
// The dialog states two consequences before the button, because a reasonable
// person would expect neither from "share with my team":
//
//   1. It cannot be undone. The only route back — clone to your personal team
//      (D6) — produces a NEW project, not the one your teammates now have open.
//   2. It exposes the Director conversation RETROACTIVELY. `chat_history` lives
//      in the project's `agent_state.json`, so publishing shares every prompt
//      ever typed into this project, not just the ones typed after. Raised in
//      review (§13.1) precisely because nothing about "share with the team"
//      implies it.
//
// ── Personal teams ─────────────────────────────────────────────────────────
//
// Shown, like anywhere else. This control used to be hidden there, and
// `publish_project` grew a matching `publish_inert_in_personal_team` refusal to
// back the hiding up with enforcement. Both are gone. The argument for them was
// that D3's flag is inert in a personal team — one member either way, so
// nothing about who can read the project changes. A personal team is invitable
// now, so it can have a roster, and publishing into it is exactly as meaningful
// as publishing into a `kind='team'` team. Leaving the refusal in place would
// have meant the people an Owner invited to their personal team could never be
// shown a single project.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Lock, Users } from 'lucide-react'

import { TeamsRequestError } from '../../api/teams'
import { useProject } from '../../api/hooks'
import { useActiveTeam } from '../../contexts/ActiveTeamContext'
import { useTeamsEnabled } from '../../features/teams/useTeamsEnabled'
import { usePublishProjectToTeam } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import Modal from '../ui/Modal'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'

export default function ProjectVisibilityControl({ projectId }: { projectId: string }) {
  const { t } = useTranslation('canvas')
  const teamsEnabled = useTeamsEnabled()
  const project = useProject(projectId)
  const { teams } = useActiveTeam()
  const publish = usePublishProjectToTeam()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const visibility = project.data?.visibility
  const ownerTeamId = project.data?.owner_team_id ?? null
  const ownerTeam = teams.find((team) => team.id === ownerTeamId) ?? null

  if (!teamsEnabled) return null
  // Absent until the project payload lands, and permanently absent on a
  // response predating the teams rollout. Rendering nothing beats rendering a
  // claim about who can read this project that we have not actually been told.
  if (visibility === undefined) return null
  // Wait for the owning team to resolve, and render nothing if it never does.
  //
  // While the team list is still loading we know the project is team-owned but
  // not WHICH team, so rendering now would show a published project as a
  // generic "Team" and then correct itself. And when `ownerTeamId` is null
  // (permanently possible under D11) there is no team to name and no team to
  // publish to, so the pill has nothing to say.
  //
  // There is deliberately no `kind === 'personal'` bail-out here. There used to
  // be, because the flag was inert in a team of one; a personal team can hold
  // invited members now, so publishing there changes exactly who can read the
  // project, same as anywhere else. `evaluate_publish_eligibility` dropped its
  // matching refusal.
  if (ownerTeam === null) return null

  const isPublished = visibility === 'team'
  const canPublish = !isPublished && project.data?.can_publish === true

  const handlePublish = () => {
    setError(null)
    const trackShare = (ok: boolean) => trackInteraction('teams_project_shared', {
      project_id: projectId, entry_point: 'project_header', result: resultOf(ok), team_id: ownerTeam.id,
    })
    publish.mutate(projectId, {
      onSuccess: () => { trackShare(true); setIsDialogOpen(false) },
      onError: (err) => {
        trackShare(false)
        if (!(err instanceof TeamsRequestError)) {
          setError(t('projectVisibility.publishFailed'))
          return
        }
        // §6 makes a private project invisible to Owners and Admins too, so
        // "you are not the creator" is the common refusal, not a bug.
        if (err.code === 'only_the_creator_can_publish') {
          setError(t('projectVisibility.onlyCreatorCanPublish'))
          return
        }
        // `owner_team_id` is permanently nullable (D11), so "this project has
        // no team to publish to" is a standing case, not a migration artefact.
        if (err.code === 'project_has_no_team') {
          setError(t('projectVisibility.projectHasNoTeam'))
          return
        }
        setError(err.message_ ?? t('projectVisibility.publishFailed'))
      },
    })
  }

  const pillClasses = cn(
    'flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
    isPublished
      ? 'border-chrome/40 bg-chrome/10 text-chrome'
      : 'border-border-subtle bg-surface-1 text-muted-foreground',
  )

  return (
    <>
      {canPublish ? (
        <button
          type="button"
          data-testid="project-visibility-pill"
          onClick={() => {
            setError(null)
            setIsDialogOpen(true)
          }}
          title={t('projectVisibility.publishAction')}
          className={cn(pillClasses, 'cursor-pointer transition-colors hover:bg-surface-3')}
        >
          <Lock className="h-3 w-3" aria-hidden="true" />
          {t('projectVisibility.private')}
        </button>
      ) : (
        <span data-testid="project-visibility-pill" className={pillClasses}>
          {isPublished ? (
            <Users className="h-3 w-3" aria-hidden="true" />
          ) : (
            <Lock className="h-3 w-3" aria-hidden="true" />
          )}
          {isPublished ? ownerTeam.name : t('projectVisibility.private')}
        </span>
      )}

      <Modal
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={t('projectVisibility.dialogTitle')}
        size="sm"
      >
        <div className="flex flex-col gap-3" data-testid="project-publish-dialog">
          <p className="text-sm leading-6 text-foreground">
            {t('projectVisibility.dialogBody', { team: ownerTeam.name })}
          </p>

          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm leading-6 text-foreground">
            <p data-testid="project-publish-oneway">
              {t('projectVisibility.dialogOneWay')}
            </p>
            <p className="mt-1.5" data-testid="project-publish-director-history">
              {t('projectVisibility.dialogDirectorHistory')}
            </p>
          </div>

          {error !== null && (
            <p className="text-sm text-destructive" data-testid="project-publish-error">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              data-testid="project-publish-cancel"
              onClick={() => setIsDialogOpen(false)}
              className="h-9 cursor-pointer rounded-lg border border-input-border bg-surface-1 px-4 text-sm text-foreground transition-colors hover:bg-surface-3"
            >
              {t('projectVisibility.cancel')}
            </button>
            <button
              type="button"
              data-testid="project-publish-submit"
              disabled={publish.isPending}
              onClick={handlePublish}
              className={cn(
                'h-9 cursor-pointer rounded-lg bg-destructive px-4 text-sm font-medium text-white',
                'transition-colors hover:bg-destructive/90',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {t('projectVisibility.publishAction')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
